import React, { useState, useEffect, useCallback } from 'react';
import {
  Bell, BellRing, AlertTriangle, TrendingUp, TrendingDown,
  Plus, Trash2, RefreshCw, Loader, Search, Settings,
  Target, Zap, Shield, Flame, CheckCircle, X, Volume2, VolumeX
} from 'lucide-react';

/**
 * GEX Alerts
 * 
 * Monitor multiple symbols and alert when price approaches
 * key gamma levels (call walls, put walls, flip level).
 */

const GEXAlerts = ({ user }) => {
  const [alerts, setAlerts] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [watchlist, setWatchlist] = useState(['SPY', 'QQQ', 'AAPL', 'TSLA', 'NVDA']);
  const [loading, setLoading] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [settings, setSettings] = useState({
    alertThreshold: 1, // % distance from level
    soundEnabled: true,
    browserNotifications: false,
    checkInterval: 60 // seconds
  });
  const [showSettings, setShowSettings] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch GEX data for all watchlist symbols
  const checkGEXLevels = useCallback(async () => {
    setLoading(true);
    const newAlerts = [];
    
    for (const symbol of watchlist) {
      try {
        const response = await fetch(`${API_URL}/gamma/exposure/${symbol}`);
        const data = await response.json();
        
        if (data.success) {
          const { currentPrice, flipLevel, topCallWalls, topPutWalls } = data;
          
          // Check proximity to flip level
          if (flipLevel) {
            const distanceToFlip = Math.abs((currentPrice - flipLevel) / currentPrice * 100);
            if (distanceToFlip <= settings.alertThreshold) {
              newAlerts.push({
                id: `${symbol}-flip-${Date.now()}`,
                symbol,
                type: 'flip',
                level: flipLevel,
                currentPrice,
                distance: distanceToFlip,
                direction: currentPrice > flipLevel ? 'above' : 'below',
                message: `${symbol} is ${distanceToFlip.toFixed(2)}% from Gamma Flip ($${flipLevel.toFixed(2)})`,
                severity: distanceToFlip < 0.5 ? 'critical' : 'warning',
                timestamp: new Date()
              });
            }
          }
          
          // Check proximity to call walls
          if (topCallWalls?.length > 0) {
            const topCall = topCallWalls[0];
            const distanceToCall = Math.abs((currentPrice - topCall.strike) / currentPrice * 100);
            if (distanceToCall <= settings.alertThreshold && currentPrice < topCall.strike) {
              newAlerts.push({
                id: `${symbol}-call-${Date.now()}`,
                symbol,
                type: 'call_wall',
                level: topCall.strike,
                currentPrice,
                distance: distanceToCall,
                direction: 'approaching',
                message: `${symbol} approaching Call Wall at $${topCall.strike.toFixed(2)}`,
                severity: distanceToCall < 0.5 ? 'critical' : 'info',
                timestamp: new Date()
              });
            }
          }
          
          // Check proximity to put walls
          if (topPutWalls?.length > 0) {
            const topPut = topPutWalls[0];
            const distanceToPut = Math.abs((currentPrice - topPut.strike) / currentPrice * 100);
            if (distanceToPut <= settings.alertThreshold && currentPrice > topPut.strike) {
              newAlerts.push({
                id: `${symbol}-put-${Date.now()}`,
                symbol,
                type: 'put_wall',
                level: topPut.strike,
                currentPrice,
                distance: distanceToPut,
                direction: 'approaching',
                message: `${symbol} approaching Put Wall at $${topPut.strike.toFixed(2)}`,
                severity: distanceToPut < 0.5 ? 'critical' : 'info',
                timestamp: new Date()
              });
            }
          }
          
          // Check for gamma environment change
          const wasPositive = alerts.find(a => a.symbol === symbol)?.gammaEnvironment === 'POSITIVE';
          const isPositive = currentPrice > flipLevel;
          if (wasPositive !== undefined && wasPositive !== isPositive) {
            newAlerts.push({
              id: `${symbol}-regime-${Date.now()}`,
              symbol,
              type: 'regime_change',
              level: flipLevel,
              currentPrice,
              distance: 0,
              direction: isPositive ? 'positive' : 'negative',
              message: `${symbol} gamma regime changed to ${isPositive ? 'POSITIVE (stable)' : 'NEGATIVE (volatile)'}`,
              severity: 'critical',
              timestamp: new Date()
            });
          }
        }
      } catch (err) {
        console.error(`Failed to check ${symbol}:`, err);
      }
    }
    
    // Update alerts
    if (newAlerts.length > 0) {
      setActiveAlerts(prev => [...newAlerts, ...prev].slice(0, 50));
      
      // Play sound if enabled
      if (settings.soundEnabled && newAlerts.some(a => a.severity === 'critical')) {
        playAlertSound();
      }
      
      // Browser notification
      if (settings.browserNotifications && newAlerts.some(a => a.severity === 'critical')) {
        sendBrowserNotification(newAlerts[0]);
      }
    }
    
    setLastCheck(new Date());
    setLoading(false);
  }, [watchlist, settings, alerts, API_URL]);

  // Initial check and interval
  useEffect(() => {
    checkGEXLevels();
    
    const interval = setInterval(checkGEXLevels, settings.checkInterval * 1000);
    return () => clearInterval(interval);
  }, [checkGEXLevels, settings.checkInterval]);

  // Play alert sound
  const playAlertSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (err) {
      console.log('Audio not supported');
    }
  };

  // Send browser notification
  const sendBrowserNotification = (alert) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('GEX Alert', {
        body: alert.message,
        icon: '/favicon.ico'
      });
    }
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setSettings(prev => ({ ...prev, browserNotifications: true }));
      }
    }
  };

  // Search stocks
  const searchStocks = async (query) => {
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/stocks/search?q=${encodeURIComponent(query)}`);
      const data = await response.json();
      setSearchResults(data || []);
    } catch (err) {
      setSearchResults([]);
    }
  };

  // Add symbol to watchlist
  const addSymbol = (symbol) => {
    if (!watchlist.includes(symbol)) {
      setWatchlist(prev => [...prev, symbol]);
    }
    setNewSymbol('');
    setSearchResults([]);
    setShowSearch(false);
  };

  // Remove symbol from watchlist
  const removeSymbol = (symbol) => {
    setWatchlist(prev => prev.filter(s => s !== symbol));
  };

  // Dismiss alert
  const dismissAlert = (alertId) => {
    setActiveAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  // Clear all alerts
  const clearAllAlerts = () => {
    setActiveAlerts([]);
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return { bg: 'rgba(239, 68, 68, 0.2)', border: '#ef4444', text: '#fca5a5' };
      case 'warning': return { bg: 'rgba(249, 115, 22, 0.2)', border: '#f97316', text: '#fdba74' };
      case 'info': return { bg: 'rgba(59, 130, 246, 0.2)', border: '#3b82f6', text: '#93c5fd' };
      default: return { bg: 'rgba(100, 116, 139, 0.2)', border: '#64748b', text: '#94a3b8' };
    }
  };

  // Get alert icon
  const getAlertIcon = (type, severity) => {
    if (severity === 'critical') return <AlertTriangle size={18} />;
    switch (type) {
      case 'flip': return <Zap size={18} />;
      case 'call_wall': return <TrendingUp size={18} />;
      case 'put_wall': return <TrendingDown size={18} />;
      case 'regime_change': return <Shield size={18} />;
      default: return <Bell size={18} />;
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      borderRadius: '1rem',
      overflow: 'hidden',
      border: '1px solid rgba(239, 68, 68, 0.2)'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(249, 115, 22, 0.15) 100%)',
        borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
        padding: '1.5rem 2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                position: 'relative'
              }}>
                <BellRing size={28} color="white" />
                {activeAlerts.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    width: '20px',
                    height: '20px',
                    background: '#ef4444',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: '700',
                    color: 'white',
                    border: '2px solid #0f172a'
                  }}>
                    {activeAlerts.length}
                  </div>
                )}
              </div>
              <div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  margin: 0,
                  background: 'linear-gradient(135deg, #fff 0%, #fca5a5 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  GEX Alerts
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                  Real-time gamma level monitoring
                </p>
              </div>
            </div>
            
            {/* Status */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.5rem',
              padding: '0.375rem 0.75rem',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '2rem',
              fontSize: '0.7rem',
              fontWeight: '600',
              color: '#10b981'
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                background: '#10b981',
                borderRadius: '50%',
                boxShadow: '0 0 8px #10b981'
              }} />
              MONITORING {watchlist.length} SYMBOLS
              {lastCheck && (
                <span style={{ color: '#64748b', marginLeft: '0.5rem' }}>
                  • Last check: {lastCheck.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={() => setShowSettings(!showSettings)}
              style={{
                padding: '0.625rem 1rem',
                background: showSettings ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: `1px solid ${showSettings ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                borderRadius: '0.5rem',
                color: showSettings ? '#a78bfa' : '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <Settings size={16} />
              Settings
            </button>
            
            <button
              onClick={checkGEXLevels}
              disabled={loading}
              style={{
                padding: '0.625rem 1rem',
                background: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '0.5rem',
                color: '#ef4444',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600'
              }}
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              Check Now
            </button>
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          borderBottom: '1px solid rgba(71, 85, 105, 0.5)',
          padding: '1.25rem 2rem'
        }}>
          <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: '600', color: '#e2e8f0' }}>
            Alert Settings
          </h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
                Alert Threshold (% from level)
              </label>
              <input
                type="number"
                value={settings.alertThreshold}
                onChange={(e) => setSettings(prev => ({ ...prev, alertThreshold: parseFloat(e.target.value) || 1 }))}
                min="0.1"
                max="5"
                step="0.1"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '0.375rem',
                  color: 'white',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            
            <div>
              <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}>
                Check Interval (seconds)
              </label>
              <select
                value={settings.checkInterval}
                onChange={(e) => setSettings(prev => ({ ...prev, checkInterval: parseInt(e.target.value) }))}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: '#1e293b',
                  border: '1px solid #475569',
                  borderRadius: '0.375rem',
                  color: 'white',
                  fontSize: '0.875rem'
                }}
              >
                <option value={30}>30 seconds</option>
                <option value={60}>1 minute</option>
                <option value={120}>2 minutes</option>
                <option value={300}>5 minutes</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                color: '#94a3b8',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={settings.soundEnabled}
                  onChange={(e) => setSettings(prev => ({ ...prev, soundEnabled: e.target.checked }))}
                  style={{ accentColor: '#ef4444' }}
                />
                {settings.soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                Sound Alerts
              </label>
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                color: '#94a3b8',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={settings.browserNotifications}
                  onChange={(e) => {
                    if (e.target.checked) {
                      requestNotificationPermission();
                    } else {
                      setSettings(prev => ({ ...prev, browserNotifications: false }));
                    }
                  }}
                  style={{ accentColor: '#ef4444' }}
                />
                <Bell size={16} />
                Browser Notifications
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ padding: '1.5rem 2rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '1.5rem' }}>
          {/* Alerts List */}
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                Active Alerts ({activeAlerts.length})
              </h3>
              
              {activeAlerts.length > 0 && (
                <button
                  onClick={clearAllAlerts}
                  style={{
                    padding: '0.375rem 0.75rem',
                    background: 'rgba(71, 85, 105, 0.5)',
                    border: 'none',
                    borderRadius: '0.375rem',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.75rem'
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
            
            {activeAlerts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {activeAlerts.map((alert) => {
                  const colors = getSeverityColor(alert.severity);
                  
                  return (
                    <div
                      key={alert.id}
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '0.75rem',
                        padding: '1rem',
                        display: 'flex',
                        alignItems: 'start',
                        gap: '1rem'
                      }}
                    >
                      <div style={{ color: colors.text }}>
                        {getAlertIcon(alert.type, alert.severity)}
                      </div>
                      
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.25rem'
                        }}>
                          <span style={{ fontWeight: '700', color: colors.text }}>
                            {alert.symbol}
                          </span>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '1rem',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            color: colors.text
                          }}>
                            {alert.type.replace('_', ' ')}
                          </span>
                          <span style={{
                            padding: '0.125rem 0.5rem',
                            background: colors.border,
                            borderRadius: '1rem',
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            color: 'white'
                          }}>
                            {alert.severity}
                          </span>
                        </div>
                        
                        <p style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.85rem',
                          color: '#e2e8f0',
                          lineHeight: '1.4'
                        }}>
                          {alert.message}
                        </p>
                        
                        <div style={{
                          display: 'flex',
                          gap: '1rem',
                          fontSize: '0.75rem',
                          color: '#94a3b8'
                        }}>
                          <span>Price: ${alert.currentPrice?.toFixed(2)}</span>
                          <span>Level: ${alert.level?.toFixed(2)}</span>
                          <span>Distance: {alert.distance?.toFixed(2)}%</span>
                        </div>
                        
                        <div style={{
                          marginTop: '0.5rem',
                          fontSize: '0.65rem',
                          color: '#64748b'
                        }}>
                          {alert.timestamp?.toLocaleString()}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => dismissAlert(alert.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#64748b',
                          cursor: 'pointer',
                          padding: '0.25rem'
                        }}
                      >
                        <X size={18} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                padding: '3rem',
                textAlign: 'center',
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                border: '1px solid rgba(71, 85, 105, 0.3)'
              }}>
                <CheckCircle size={48} color="#10b981" style={{ marginBottom: '1rem' }} />
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#10b981' }}>All Clear</h4>
                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
                  No active alerts. Monitoring {watchlist.length} symbols for key gamma levels.
                </p>
              </div>
            )}
          </div>

          {/* Watchlist Sidebar */}
          <div>
            <div style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(71, 85, 105, 0.5)',
              borderRadius: '0.75rem',
              padding: '1.25rem'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: '600' }}>
                Watchlist ({watchlist.length})
              </h4>
              
              {/* Add Symbol */}
              <div style={{ position: 'relative', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newSymbol}
                    onChange={(e) => {
                      setNewSymbol(e.target.value.toUpperCase());
                      searchStocks(e.target.value);
                      setShowSearch(true);
                    }}
                    placeholder="Add symbol..."
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      background: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '0.375rem',
                      color: 'white',
                      fontSize: '0.875rem',
                      outline: 'none'
                    }}
                    onFocus={() => setShowSearch(true)}
                  />
                  <button
                    onClick={() => newSymbol && addSymbol(newSymbol)}
                    style={{
                      padding: '0.5rem',
                      background: '#10b981',
                      border: 'none',
                      borderRadius: '0.375rem',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <Plus size={18} />
                  </button>
                </div>
                
                {showSearch && searchResults.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '0.375rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 50
                  }}>
                    {searchResults.slice(0, 5).map((result, idx) => (
                      <div
                        key={idx}
                        onClick={() => addSymbol(result.symbol)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #334155'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#334155'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ fontWeight: '600', color: '#60a5fa', fontSize: '0.85rem' }}>
                          {result.symbol}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          {result.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Symbol List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {watchlist.map((symbol) => (
                  <div
                    key={symbol}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0.75rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      borderRadius: '0.375rem',
                      border: '1px solid rgba(71, 85, 105, 0.3)'
                    }}
                  >
                    <span style={{ fontWeight: '600', color: '#e2e8f0' }}>{symbol}</span>
                    <button
                      onClick={() => removeSymbol(symbol)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#64748b',
                        cursor: 'pointer',
                        padding: '0.25rem'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Alert Types Info */}
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '0.5rem'
            }}>
              <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#93c5fd' }}>
                Alert Types
              </h5>
              <div style={{ fontSize: '0.7rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                <div style={{ marginBottom: '0.375rem' }}>
                  <Zap size={12} style={{ display: 'inline', marginRight: '0.375rem' }} />
                  <strong>Flip Level:</strong> Near gamma regime change
                </div>
                <div style={{ marginBottom: '0.375rem' }}>
                  <TrendingUp size={12} style={{ display: 'inline', marginRight: '0.375rem' }} />
                  <strong>Call Wall:</strong> Approaching resistance
                </div>
                <div style={{ marginBottom: '0.375rem' }}>
                  <TrendingDown size={12} style={{ display: 'inline', marginRight: '0.375rem' }} />
                  <strong>Put Wall:</strong> Approaching support
                </div>
                <div>
                  <Shield size={12} style={{ display: 'inline', marginRight: '0.375rem' }} />
                  <strong>Regime Change:</strong> Gamma flipped
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Add spin animation
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .spin {
    animation: spin 1s linear infinite;
  }
`;
if (typeof document !== 'undefined') {
  document.head.appendChild(style);
}

export default GEXAlerts;